import React, {
  ChangeEvent,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { ChangeHandler } from 'react-hook-form';

import './SelectField.css';

type SelectFieldProps<T> = {
  options: T[];
  displayFn: (option: T) => string;
  valueFn: (option: T) => string;
  name: string;
  value?: T;
  onChange?: ChangeHandler;
  onBlur?: ChangeHandler;
  onValueChange: (val: T) => void;
  placeholder: string;
  error?: string;
  disabled?: boolean;
};

type Option = {
  label: string;
  value: string;
};

export const SelectField = forwardRef<HTMLSelectElement, any>(
  <T,>(
    {
      options,
      name,
      value,
      placeholder,
      displayFn,
      valueFn,
      onValueChange,
      onChange,
      onBlur,
      error,
      disabled = false,
    }: SelectFieldProps<T>,
    ref: any
  ) => {
    const selectRef = useRef<HTMLSelectElement>(null);
    useImperativeHandle(ref, () => selectRef.current!);

    const refinedOptions: Option[] = useMemo(() => {
      return options.map((o) => ({
        label: displayFn(o),
        value: valueFn(o),
      }));
    }, [displayFn, options, valueFn]);

    const onSelectValueChange = useCallback(
      (e: ChangeEvent<HTMLSelectElement>) => {
        onChange?.(e);
        const selectedOption = options.find(
          (o) => e.target.value === valueFn(o)
        );
        onValueChange?.(selectedOption!);
      },
      [onChange, onValueChange, options, valueFn]
    );

    const selectClass = useMemo(() => {
      return `select-field--input ${error ? 'select-field--input-error' : ''}`;
    }, [error]);

    const labelClass = useMemo(() => {
      return `select-field--label ${error ? 'select-field--label-error' : ''}`;
    }, [error]);

    const normalizedValue = useMemo(() => {
      return value ? valueFn(value) : undefined;
    }, [value, valueFn]);

    return (
      <div className="select-field--container">
        <div className="select-field--group">
          <select
            ref={selectRef}
            className={selectClass}
            placeholder={placeholder}
            onChange={onSelectValueChange}
            onBlur={onBlur}
            name={name}
            defaultValue=""
            value={normalizedValue}
            id={`select-field-${name}`}
            disabled={disabled}
          >
            <option value="" disabled hidden>
              Seleziona uno tra questi...
            </option>
            {refinedOptions.map((o) => (
              <React.Fragment key={o.value}>
                <option value={o.value}>{o.label}</option>
              </React.Fragment>
            ))}
          </select>
          <label htmlFor={`select-field-${name}`} className={labelClass}>
            {placeholder}
          </label>
        </div>
        {error && <p className="select-field--error-message">{error}</p>}
      </div>
    );
  }
);
